import BaseService from "react-lib/apis/services/BaseService";

import { normalizePurRecord, PurRecord } from "./purApi";
import { PurDraftCreateInput, PurDraftKind } from "./purDraftCreatePilot";
import { getPur, PUR_REQUEST_TIMEOUT_MS, PurClientConfig, purRequestOptions, readId, readObject, readText, requireId } from "./purRead";

export type { PurDraftCreateInput, PurDraftKind } from "./purDraftCreatePilot";
export interface PurDraftCreatePermission { resource: boolean; action: boolean }
export type PurDraftCreatePermissions = Record<PurDraftKind, PurDraftCreatePermission>;
export interface PurDraftCreateAttempt { input: PurDraftCreateInput; marker: string }

// The frontend offers this workflow for the authenticated current division.
// The server remains the authority for object-level permissions.
export { PUR_CREATE_PILOT } from "./purDraftCreatePilot";
const paths: Record<PurDraftKind, string> = { materialRequests: "material-requests", purchaseOrders: "purchase-orders" };
export const purDraftPermissionNames = (kind: PurDraftKind): { resource: string; action: string } => ({
  resource: `users.CREATE_PUR:${kind === "materialRequests" ? "material-request-list" : "purchase-order-list"}`,
  action: `users.ACTION_PUR.${kind === "materialRequests" ? "MaterialRequest" : "PurchaseOrder"}:CREATE`,
});
const keys = (kind: PurDraftKind): { resource: string; action: string } => ({
  resource: `perm_users_CREATE_PUR:${kind === "materialRequests" ? "material-request-list" : "purchase-order-list"}`,
  action: `action_PUR_${kind === "materialRequests" ? "MaterialRequest" : "PurchaseOrder"}_CREATE`,
});

export class PurDraftCreateError extends Error {
  constructor(public outcome: "blocked" | "rejected" | "unknown", public messageId: string, public recordId: number | null = null) {
    super(messageId);
    Object.setPrototypeOf(this, PurDraftCreateError.prototype);
  }
}
const block = (id = "MEERP_CREATE_SCOPE_ERROR"): never => { throw new PurDraftCreateError("blocked", id); };
export const canUsePurDraftCreatePilot = (client: PurClientConfig): boolean =>
  Boolean(client.apiToken?.trim()) && readId(client.divisionId) !== null;

export const checkPurDraftCreatePermissions = async (client: PurClientConfig): Promise<PurDraftCreatePermissions> => {
  if (!canUsePurDraftCreatePilot(client)) return block();
  const request: Record<string, boolean> = {};
  (["materialRequests", "purchaseOrders"] as const).forEach((kind) => {
    request[keys(kind).resource] = false; request[keys(kind).action] = false;
  });
  const { url, headers } = purRequestOptions({ ...client, pathPrefix: "/users/apis" }, "user-permission/");
  // POST here is the existing read-only permission query, not a PUR mutation.
  const response = await new BaseService().client.post(url, request, { headers, timeout: PUR_REQUEST_TIMEOUT_MS });
  const data = readObject(response.data);
  const permission = (kind: PurDraftKind): PurDraftCreatePermission => ({
    resource: data[keys(kind).resource] === true, action: data[keys(kind).action] === true,
  });
  return { materialRequests: permission("materialRequests"), purchaseOrders: permission("purchaseOrders") };
};

const decimal = (value: string | undefined, places: number, positive = false, maxDigits = 12): string => {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value?.trim() || "");
  if (!match || (match[2] || "").slice(places).replace(/0/g, "")) return block("MEERP_CREATE_VALIDATION_ERROR");
  const integer = match[1].replace(/^0+(?=\d)/, "");
  const fraction = (match[2] || "").slice(0, places).padEnd(places, "0");
  if (integer.length > maxDigits - places || (positive && !/[1-9]/.test(integer + fraction))) return block("MEERP_CREATE_VALIDATION_ERROR");
  return `${integer}.${fraction}`;
};
const date = (value: string): string => {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) return block("MEERP_CREATE_VALIDATION_ERROR");
  return value;
};
const rate = (value: string | undefined): string => {
  const result = decimal(value, 2, false, 5);
  if (Number(result) > 100) return block("MEERP_CREATE_VALIDATION_ERROR");
  return result;
};
export const buildPurDraftCreatePayload = (attempt: PurDraftCreateAttempt): Record<string, unknown> => {
  const { input, marker } = attempt;
  const isOrder = input.kind === "purchaseOrders";
  if (!(["materialRequests", "purchaseOrders"] as string[]).includes(input.kind)
    || !/^MEERP-CREATE-[A-Za-z0-9-]+$/.test(marker) || !input.lines.length || input.lines.length > 100) return block();
  const headerVat = isOrder ? rate(input.vatRate) : null;
  const seen = new Set<number>();
  const items = input.lines.map((line) => {
    const product = requireId(line.productId), unit = requireId(line.unitId);
    if (seen.has(product)) return block("MEERP_DRAFT_DUPLICATE_ERROR");
    seen.add(product);
    const common = { product, unit, conversion_factor: decimal(line.factor, 4, true) };
    // PUR totals use only the header VAT. Derive every line from that single
    // source, including when an older draft contains an obsolete line rate.
    return isOrder ? { ...common, ordered_qty: decimal(line.quantity, 2, true), unit_cost: decimal(line.unitCost, 2),
      discount: decimal(line.discount || "0", 2), vat_rate: headerVat }
      : { ...common, requested_qty: decimal(line.quantity, 2, true) };
  });
  const text = isOrder ? input.text.trim() : [marker, input.text.trim()].filter(Boolean).join("\n");
  const delivery = input.deliveryDate ? date(input.deliveryDate) : null;
  // Explicit allowlist: no client ID, official number, user, status, links,
  // calculated totals or arbitrary action can leak into a create request.
  return isOrder ? { action: "CREATE", project: input.projectId, vendor: input.vendorId,
    delivery_storage: input.storageId, order_date: date(input.date), expected_delivery_date: delivery,
    vat_rate: headerVat, notes: text, items }
    : { action: "CREATE", project: input.projectId, requesting_division: requireId(input.requestingDivisionId),
      target_storage: input.storageId, request_date: date(input.date), required_date: delivery, purpose: text, items };
};

const ensurePermission = async (client: PurClientConfig, kind: PurDraftKind): Promise<void> => {
  const permission = (await checkPurDraftCreatePermissions(client))[kind];
  if (!permission.resource || !permission.action) return block("MEERP_CREATE_PERMISSION_MISSING");
};
const ensureProject = async (client: PurClientConfig, input: PurDraftCreateInput): Promise<void> => {
  const project = readObject(await getPur(client, `projects/${input.projectId}/`));
  if (input.kind === "materialRequests" && (readId(input.requestingDivisionId) !== readId(client.divisionId)
    || readId(project.id) !== input.projectId || readId(project.division) !== readId(client.divisionId) || project.active !== true)) return block();
  if (input.kind === "purchaseOrders" && (readId(project.id) !== input.projectId || readId(project.division) !== readId(client.divisionId) || project.active !== true)) return block();
  if (input.kind === "purchaseOrders") {
    const vendor = readObject(await getPur(client, `vendors/${input.vendorId}/`));
    if (readId(vendor.id) !== input.vendorId) return block();
  }
};

export const reconcilePurDraftCreate = async (client: PurClientConfig, attempt: PurDraftCreateAttempt, id: number): Promise<PurRecord> => {
  if (!canUsePurDraftCreatePilot(client)) return block();
  const payload = buildPurDraftCreatePayload(attempt);
  const raw = readObject(await getPur(client, `${paths[attempt.input.kind]}/${requireId(id)}/`));
  const isOrder = attempt.input.kind === "purchaseOrders";
  const record = normalizePurRecord(attempt.input.kind, raw);
  const sameDecimal = (left: unknown, right: unknown, places: number): boolean => {
    try { return decimal(readText(left), places) === decimal(readText(right), places); } catch { return false; }
  };
  const expectedItems = payload.items as Array<Record<string, unknown>>;
  const rows = Array.isArray(raw.items) ? raw.items.map(readObject) : [];
  const qtyField = isOrder ? "ordered_qty" : "requested_qty";
  const numberField = isOrder ? "po_number" : "request_number";
  const matches = record.id === id && record.status === "DRAFT" && Boolean(readText(raw[numberField]).trim())
    && readId(raw.project) === attempt.input.projectId && readId(raw[isOrder ? "delivery_storage" : "target_storage"]) === attempt.input.storageId
    && raw[isOrder ? "order_date" : "request_date"] === attempt.input.date
    && (raw[isOrder ? "expected_delivery_date" : "required_date"] ?? null) === payload[isOrder ? "expected_delivery_date" : "required_date"]
    && raw[isOrder ? "notes" : "purpose"] === payload[isOrder ? "notes" : "purpose"]
    && (isOrder ? readId(raw.vendor) === attempt.input.vendorId && sameDecimal(raw.vat_rate, payload.vat_rate, 2)
      && sameDecimal(raw.per_received, "0", 2) && sameDecimal(raw.per_billed, "0", 2)
      : readId(raw.requesting_division) === readId(attempt.input.requestingDivisionId))
    && rows.length === expectedItems.length && new Set(rows.map((row) => readId(row.product))).size === rows.length
    && new Set(rows.map((row) => readId(row.id))).size === rows.length
    && expectedItems.every((expected) => {
      const row = rows.find((item) => readId(item.product) === expected.product);
      return row && readId(row.id) !== null && readId(row.unit) === expected.unit
        && sameDecimal(row[qtyField], expected[qtyField], 2) && sameDecimal(row.conversion_factor, expected.conversion_factor, 4)
        && (isOrder ? sameDecimal(row.unit_cost, expected.unit_cost, 2) && sameDecimal(row.discount, expected.discount, 2)
          && sameDecimal(row.vat_rate, expected.vat_rate, 2) && sameDecimal(row.received_qty, "0", 2) && sameDecimal(row.invoiced_qty, "0", 2)
          : sameDecimal(row.ordered_qty, "0", 2) && row.po_item === null);
    });
  if (!matches) throw new PurDraftCreateError("unknown", "MEERP_CREATE_READBACK_ERROR", id);
  return record;
};

export const createPurDraft = async (
  client: PurClientConfig, attempt: PurDraftCreateAttempt, isCurrent: () => boolean = () => true,
): Promise<PurRecord> => {
  if (!canUsePurDraftCreatePilot(client)) return block();
  const payload = buildPurDraftCreatePayload(attempt);
  // Recheck at commit time, not just when the form was opened.
  await ensurePermission(client, attempt.input.kind);
  if (!isCurrent()) return block("MEERP_CREATE_CONTEXT_CHANGED");
  await ensureProject(client, attempt.input);
  if (!isCurrent()) return block("MEERP_CREATE_CONTEXT_CHANGED");
  const { url, headers } = purRequestOptions(client, `${paths[attempt.input.kind]}/`);
  let returnedId: number | null = null;
  try {
    const response = await new BaseService().client.post(url, payload, { headers, timeout: PUR_REQUEST_TIMEOUT_MS });
    returnedId = readId(readObject(response.data).id);
    if (response.status !== 201 || returnedId === null) throw new PurDraftCreateError("unknown", "MEERP_CREATE_UNKNOWN", returnedId);
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    // A timeout, 5xx, unexpected success or transport error can follow a commit.
    // Never automatically retry a mutation; require GET reconciliation instead.
    if (status && [400, 401, 403, 404, 405, 422].includes(status)) {
      throw new PurDraftCreateError("rejected", status === 401 || status === 403 ? "MEERP_CREATE_PERMISSION_MISSING" : "MEERP_CREATE_REJECTED");
    }
    throw new PurDraftCreateError("unknown", "MEERP_CREATE_UNKNOWN", returnedId);
  }
  try { return await reconcilePurDraftCreate(client, attempt, returnedId); }
  catch { throw new PurDraftCreateError("unknown", "MEERP_CREATE_READBACK_ERROR", returnedId); }
};
